// src/pages/maintenance/AssetModal.js
// Create/edit modal for a MaintenanceAsset — shared between MaintenanceAssetsPage (list) and
// MaintenanceAssetDetailPage (edit-in-place). See docs/MAINTENANCE_MODULE.md.
import React, { useState } from 'react';
import maintenanceService from '../../services/maintenanceService';
import SearchableSelect from '../../components/SearchableSelect';

const CATEGORY_OPTIONS = [
  'oven', 'freezer', 'refrigerator', 'mixer', 'conveyor', 'compressor',
  'packaging_machine', 'scale', 'metal_detector', 'thermometer', 'other',
];
const STATUS_OPTIONS = ['operational', 'under_maintenance', 'out_of_service', 'decommissioned'];
const CRITICALITY_OPTIONS = ['critical', 'high', 'medium', 'low'];

const labelize = (s) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const SUPPRESSING_STATUSES = ['under_maintenance', 'out_of_service'];

// datetime-local inputs give "YYYY-MM-DDTHH:mm" in the browser's own local time, with no
// timezone info — a raw `new Date(value)` on an input like that is ambiguous (parsed as the
// SERVER's local time once it reaches the backend, not the browser's), so round-trip through
// explicit local Date components on both ends instead of trusting a bare string parse.
const isoToDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const datetimeLocalToISO = (value) => {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = (timePart || '00:00').split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm).toISOString();
};

export default function AssetModal({ asset, locations = [], onSave, onClose }) {
  const [form, setForm] = useState({
    name: asset?.name || '',
    category: asset?.category || 'other',
    make: asset?.make || '',
    model: asset?.model || '',
    serialNumber: asset?.serialNumber || '',
    purchaseDate: asset?.purchaseDate || '',
    warrantyExpiryDate: asset?.warrantyExpiryDate || '',
    locationId: asset?.locationId || '',
    status: asset?.status || 'operational',
    statusSince: isoToDatetimeLocal(asset?.statusSince),
    statusReason: asset?.statusReason || '',
    criticalityLevel: asset?.criticalityLevel || 'medium',
    isFoodSafetyCritical: !!asset?.isFoodSafetyCritical,
    notes: asset?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedResult, setSavedResult] = useState(null); // holds the response only when there's an auto-ack count worth confirming
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // No dedicated Locations management page exists yet — this is the only way to create one.
  const [localLocations, setLocalLocations] = useState(locations);
  const [showNewLocation, setShowNewLocation] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [addingLocation, setAddingLocation] = useState(false);

  const addLocation = async () => {
    if (!newLocationName.trim()) return;
    setAddingLocation(true);
    try {
      const created = await maintenanceService.createLocation({ name: newLocationName.trim() });
      setLocalLocations(list => [...list, created]);
      set('locationId', created.id);
      setNewLocationName('');
      setShowNewLocation(false);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create location.');
    } finally {
      setAddingLocation(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        locationId: form.locationId || null,
        statusSince: SUPPRESSING_STATUSES.includes(form.status) ? datetimeLocalToISO(form.statusSince) : null,
      };
      const result = asset?.id
        ? await maintenanceService.updateAsset(asset.id, payload)
        : await maintenanceService.createAsset(payload);
      // Worth an explicit confirmation rather than silently closing — this count is often the
      // whole point of backdating statusSince (clearing a real backlog of pending alarms).
      if (result.autoAcknowledgedReadingsCount > 0) {
        setSavedResult(result);
      } else {
        onSave(result);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save asset.');
    } finally {
      setSaving(false);
    }
  };

  if (savedResult) {
    return (
      <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">✓ Asset Updated</h5>
            </div>
            <div className="modal-body text-center py-4">
              <div className="mb-2" style={{ fontSize: 32 }}>✅</div>
              <div>
                <strong>{savedResult.autoAcknowledgedReadingsCount}</strong> pending temperature
                alarm{savedResult.autoAcknowledgedReadingsCount === 1 ? '' : 's'} auto-acknowledged.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => onSave(savedResult)}>Done</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{asset?.id ? 'Edit' : 'New'} Asset</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Name <span className="text-danger">*</span></label>
                  <input className="form-control form-control-sm" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Walk-in Freezer #2" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Category</label>
                  <SearchableSelect
                    options={CATEGORY_OPTIONS.map(c => ({ value: c, label: labelize(c) }))}
                    value={form.category}
                    onChange={val => set('category', val || 'other')}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Make</label>
                  <input className="form-control form-control-sm" value={form.make} onChange={e => set('make', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Model</label>
                  <input className="form-control form-control-sm" value={form.model} onChange={e => set('model', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Serial Number</label>
                  <input className="form-control form-control-sm" value={form.serialNumber} onChange={e => set('serialNumber', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Purchase Date</label>
                  <input type="date" className="form-control form-control-sm" value={form.purchaseDate || ''} onChange={e => set('purchaseDate', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Warranty Expiry</label>
                  <input type="date" className="form-control form-control-sm" value={form.warrantyExpiryDate || ''} onChange={e => set('warrantyExpiryDate', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Location</label>
                    <button type="button" className="btn btn-link btn-sm p-0 mb-1" style={{ fontSize: 12 }} onClick={() => setShowNewLocation(v => !v)}>
                      {showNewLocation ? 'Cancel' : '+ New'}
                    </button>
                  </div>
                  {showNewLocation ? (
                    <div className="input-group input-group-sm">
                      <input
                        className="form-control"
                        placeholder="Location name"
                        value={newLocationName}
                        onChange={e => setNewLocationName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addLocation())}
                        autoFocus
                      />
                      <button type="button" className="btn btn-outline-primary" disabled={addingLocation || !newLocationName.trim()} onClick={addLocation}>
                        {addingLocation ? '…' : 'Add'}
                      </button>
                    </div>
                  ) : (
                    <SearchableSelect
                      options={localLocations.map(l => ({ value: l.id, label: l.name }))}
                      value={form.locationId}
                      onChange={val => set('locationId', val || '')}
                      placeholder="No location"
                    />
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Status</label>
                  <SearchableSelect
                    options={STATUS_OPTIONS.map(s => ({ value: s, label: labelize(s) }))}
                    value={form.status}
                    onChange={val => {
                      const newStatus = val || 'operational';
                      setForm(f => ({
                        ...f,
                        status: newStatus,
                        // Prefill "now" the moment this asset first enters a suppressing status —
                        // the user can still backdate it below if the real repair started earlier.
                        statusSince: (SUPPRESSING_STATUSES.includes(newStatus) && !f.statusSince)
                          ? isoToDatetimeLocal(new Date().toISOString())
                          : f.statusSince,
                      }));
                    }}
                  />
                  {SUPPRESSING_STATUSES.includes(form.status) && (
                    <div className="mt-2">
                      <label className="form-label fw-semibold" style={{ fontSize: 12 }}>
                        {labelize(form.status)} since
                      </label>
                      <input type="datetime-local" className="form-control form-control-sm"
                        value={form.statusSince} onChange={e => set('statusSince', e.target.value)}
                        max={isoToDatetimeLocal(new Date().toISOString())} />
                      <input type="text" className="form-control form-control-sm mt-1" placeholder="Reason (optional) — e.g. technician requested shutdown to defrost evaporator"
                        value={form.statusReason} onChange={e => set('statusReason', e.target.value)} />
                      <div className="form-text" style={{ fontSize: 11 }}>
                        🔧 Backdate this if the equipment was actually taken down earlier than now —
                        pending temperature alarms recorded from that time forward will be
                        auto-acknowledged in one shot, and new ones auto-acknowledged too, until
                        this is set back to Operational.
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Criticality</label>
                  <SearchableSelect
                    options={CRITICALITY_OPTIONS.map(c => ({ value: c, label: labelize(c) }))}
                    value={form.criticalityLevel}
                    onChange={val => set('criticalityLevel', val || 'medium')}
                  />
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input" id="fsCritical" checked={form.isFoodSafetyCritical} onChange={e => set('isFoodSafetyCritical', e.target.checked)} />
                    <label className="form-check-label" htmlFor="fsCritical" style={{ fontSize: 13 }}>
                      Food-safety critical (appears in the Food Safety Plan's Equipment List)
                    </label>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Notes</label>
                  <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save Asset'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
